import { Badge, Button, Card, ProgressBar, Text } from '@shopify/polaris';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import type { ProductFitmentCopyJob } from '../../app/models/Product';

const terminalStates = new Set(['Completed', 'CompletedWithWarnings', 'Failed', 'Cancelled']);

const showToast = (message: string) => {
    try {
        // Shopify's Toast API takes the message as its first argument and the
        // display options separately. Keep completion feedback best-effort so
        // an unavailable or changing host API can never break the app page.
        (window as any).shopify?.toast?.show?.(message, { duration: 5000 });
    } catch (error) {
        console.warn('Unable to show fitment copy completion toast.', error);
    }
};

/**
 * App-wide activity indicator for durable fitment copies. It deliberately uses
 * polling rather than an in-memory event channel so navigation and API restarts
 * cannot disconnect the UI from the persisted job state.
 */
const ProductFitmentCopyActivity = () => {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState<ProductFitmentCopyJob[]>([]);
    const previousStates = useRef<Map<number, string>>(new Map());
    const initialized = useRef(false);

    useEffect(() => {
        let disposed = false;
        let timeoutId: number | undefined;

        const poll = async () => {
            let hasActiveJobs = false;
            try {
                const nextJobs = await agent.Products.listRecentFitmentCopyJobs(false, 10);
                if (disposed) return;

                hasActiveJobs = nextJobs.some((job) => job.isActive);
                if (initialized.current) {
                    nextJobs.forEach((job) => {
                        const previousState = previousStates.current.get(job.id);
                        if (previousState && !terminalStates.has(previousState) && terminalStates.has(job.state)) {
                            showToast(job.state === 'Completed'
                                ? `${job.totalFitments.toLocaleString()} fitments copied to ${job.targetProductTitle}.`
                                : job.message);
                        }
                    });
                }

                previousStates.current = new Map(nextJobs.map((job) => [job.id, job.state]));
                initialized.current = true;
                setJobs(nextJobs);
            } catch {
                // Product pages surface actionable API errors. The global status
                // indicator stays quiet during transient polling failures.
            } finally {
                if (!disposed) {
                    const delay = document.visibilityState === 'hidden'
                        ? 15000
                        : hasActiveJobs ? 3000 : 15000;
                    timeoutId = window.setTimeout(poll, delay);
                }
            }
        };

        poll();
        return () => {
            disposed = true;
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, []);

    const activeJobs = useMemo(() => jobs.filter((job) => job.isActive), [jobs]);
    if (activeJobs.length === 0) return null;

    const job = activeJobs[0];
    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                right: 24,
                bottom: 24,
                width: 'min(380px, calc(100vw - 32px))',
                zIndex: 520,
                boxShadow: '0 8px 28px rgba(0, 0, 0, 0.18)',
                borderRadius: 12,
            }}
        >
            <Card>
                <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <Text as="h3" variant="headingSm">Copying fitments</Text>
                        <Badge tone="info">{activeJobs.length > 1 ? `${activeJobs.length} running` : job.state}</Badge>
                    </div>
                    <Text as="p" variant="bodySm">
                        {job.sourceProductTitle} → {job.targetProductTitle}
                    </Text>
                    <ProgressBar progress={job.progressPercent} size="small" />
                    <Text as="p" variant="bodySm" tone="subdued">{job.message}</Text>
                    <div>
                        <Button variant="plain" onClick={() => navigate(`/products/${job.targetProductId}`)}>
                            View product
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default ProductFitmentCopyActivity;
